import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { DatasetKey } from "@nexxt/contracts/reference";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the reference data (50-api/reference.md): the records of a dataset by page, and a dataset whole
// for the names of the records another one points to.

const records = api.reference.records;

type ListPage = InferResponseType<typeof records.$get, 200>;
export type ReferenceRecord = ListPage["items"][number];

/** The dataset the screen opens on. */
export const DEFAULT_DATASET: DatasetKey = "season";

const PAGE_SIZE = 100;
/** The largest page the gateway gives: a dataset is read whole in as few requests as it takes. */
const WHOLE_PAGE = 500;

const keys = { all: ["reference"] as const };

const page = (dataset: DatasetKey, query: { q?: string; page: number; pageSize: number }, signal: AbortSignal) =>
  unwrap(
    records.$get(
      {
        query: {
          dataset,
          ...(query.q && { q: query.q }),
          page: String(query.page),
          pageSize: String(query.pageSize),
        },
      },
      { init: { signal } },
    ),
  );

export const referenceQueries = {
  ...keys,

  list: (dataset: DatasetKey, q: string | undefined) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, dataset, "list", { q }],
      queryFn: ({ pageParam, signal }) =>
        page(dataset, { ...(q && { q }), page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** Every record of a dataset: what a select offers and what a cell names. */
  whole: (dataset: DatasetKey) =>
    queryOptions({
      queryKey: [...keys.all, dataset, "whole"],
      queryFn: async ({ signal }) => {
        const items: ReferenceRecord[] = [];
        for (let next = 1, more = true; more; next++) {
          const read = await page(dataset, { page: next, pageSize: WHOLE_PAGE }, signal);
          items.push(...read.items);
          more = read.hasMore;
        }
        return items;
      },
    }),
};
